import { formatNumber } from "../../utils";
import {
  isFuture,
  nextSunday,
  previousSunday,
  startOfDay,
  startOfYesterday,
  sub,
} from "date-fns";
import { nextSaturday } from "date-fns/esm";
import { DateAccumulatorInterface, UsageDataInterface } from "./ReportTypes";

const HOUR = 3600;
export const DAY = 86400; // number of seconds in a day
const WEEK = DAY * 7;
const MONTH = DAY * 30;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const user = JSON.parse(localStorage.getItem("user") || "{}");

export type YearOptions = "2023-2024" | "2022-2023";

export const getPerformanceDateRange = (year: YearOptions) => {
  const [start, end] = year
    .split("-")
    .map((year: string) => parseInt(year.trim()));

  const beginning = new Date(start, 7, 1, 0, 0);
  let ending = new Date(end, 6, 31, 23, 59, 59);
  const currentYear = new Date().getFullYear();

  // end is in current year, don't go further than today
  if (end === currentYear) {
    ending = new Date();
  }
  return { beginning, ending };
};

export const getStartEndDate = (): {
  yesterday: Date;
  lastWeek: Date;
  thirtyDays: Date;
  yearToDate: Date;
  lastYearToDate: Date;
  lastYearEnd: Date;
  currentkey: string;
  lastKey: string;
  today: Date;
} => {
  const today = new Date();
  const yesterday = startOfYesterday();
  const lastWeek = sub(yesterday, { days: 6 });
  const thirtyDays = sub(yesterday, { days: 30 });

  let yearToDate = new Date();
  const augustOfCurrentYear = new Date(today.getFullYear(), 7, 1);
  yearToDate = augustOfCurrentYear;
  if (isFuture(augustOfCurrentYear)) {
    yearToDate = new Date(today.getFullYear() - 1, 7, 1);
  }
  const lastYearToDate = new Date(yearToDate.getFullYear() - 1, 7, 1);
  const lastYearEnd = new Date(yearToDate.getFullYear(), 6, 31, 23, 59, 59);
  const currentkey = `${yearToDate.getFullYear().toString(10).slice(2)}-${(
    yearToDate.getFullYear() + 1
  )
    .toString(10)
    .slice(2)}`;
  const lastKey = `${lastYearToDate.getFullYear().toString(10).slice(2)}-${(
    lastYearToDate.getFullYear() + 1
  )
    .toString(10)
    .slice(2)}`;
  return {
    yesterday: yesterday,
    today: today,
    lastWeek: lastWeek,
    thirtyDays: thirtyDays,
    yearToDate: yearToDate,
    lastYearToDate: lastYearToDate,
    lastYearEnd: lastYearEnd,
    currentkey: currentkey,
    lastKey: lastKey,
  };
};

export const getDateInterval = ({
  start,
  end,
}: {
  start: number;
  end: number;
}): string => {
  // determine what size time interval by which to accumulate data
  let interval = "";
  if (end - start < DAY) {
    interval = "hour";
  } else if (end - start < DAY * 14) {
    // less than 2 weeks
    interval = "day";
  }
  if (end - start >= DAY * 14) {
    // 2 weeks or more
    interval = "week";
  }
  if (end - start > DAY * 90) {
    // more than 3ish months
    interval = "month";
  }
  return interval;
};

export const getAggregateDateLabels = ({
  interval,
  acc,
}: {
  interval: string;
  // acc: DateAccumulatorInterface[];
  acc: Date[];
}) => {
  const labels: string[] = [];
  interval === "hour"
    ? acc.map((entry) =>
        labels.push(
          // "en-GB" for military time, "en-US" (doesn't quite fit in-line) for 12 hour with AM/PM
          `${entry.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
          })}`
        )
      )
    : interval === "day"
    ? acc.map((entry) =>
        labels.push(
          `${entry.getMonth() + 1}/${entry.getDate()}/${entry.getFullYear()}`
        )
      )
    : interval === "week"
    ? acc.map((entry) =>
        // week = Sunday -- Saturday
        labels.push(
          `${entry.getMonth() + 1}/${entry.getDate()} - ${
            nextSaturday(entry).getMonth() + 1 // getMonth() returns 0 indexed month -> add 1
          }/${nextSaturday(entry).getDate()}`
        )
      )
    : acc.map((entry) =>
        labels.push(`${MONTHS[entry.getMonth()]} ${entry.getFullYear()}`)
      );

  return labels;
};

export const accumulateUsageData = ({
  data,
  dateRange,
}: {
  data: UsageDataInterface[];
  dateRange: { start: number; end: number };
}): {
  acc: Array<DateAccumulatorInterface>;
  aggregateDateLabels: any;
  aggregateTimeAccrued: any;
  aggregateProblemsSolved: any;
} => {
  const interval = getDateInterval({
    start: dateRange.start,
    end: dateRange.end,
  });

  if (data.length < 1) {
    const acc: never[] = [];
    const aggregateDateLabels: never[] = [];
    const aggregateTimeAccrued: never[] = [];
    const aggregateProblemsSolved: never[] = [];
    return {
      acc,
      aggregateDateLabels,
      aggregateTimeAccrued,
      aggregateProblemsSolved,
    };
  }

  // construct interval buckets
  const dateIntervals = [new Date(dateRange.start * 1000)];
  if (interval === "hour") {
    // use start of the day so that labels go from 12:00AM - 11:00PM
    dateIntervals[0] = startOfDay(dateIntervals[0]);
  }
  if (interval === "hour") {
    let current = dateIntervals[0];
    for (let i = 0; i < 23; i++) {
      const next = new Date((current.getTime() / 1000 + HOUR) * 1000);
      dateIntervals.push(next);
      current = next;
    }
  } else if (interval === "day") {
    let current = new Date(dateRange.start * 1000);
    while (current.getTime() / 1000 < dateRange.end) {
      const nextDay = new Date(current);
      nextDay.setDate(current.getDate() + 1);
      dateIntervals.push(nextDay);
      current = nextDay;
    }
  } else if (interval === "week") {
    if (dateIntervals[0].getDay() !== 0) {
      dateIntervals[0] = previousSunday(dateIntervals[0]);
    }
    const startDay = new Date(dateRange.start * 1000);
    let current = startDay.getDay() !== 0 ? previousSunday(startDay) : startDay; //
    while (current.getTime() / 1000 < dateRange.end) {
      const nextWeek = nextSunday(current);
      nextWeek.getTime() / 1000 < dateRange.end && dateIntervals.push(nextWeek);
      current = nextWeek;
    }
  } else if (interval === "month") {
    let current = new Date(dateRange.start * 1000);
    while (current.getTime() / 1000 < dateRange.end) {
      const nextMonth = new Date(current);
      nextMonth.setMonth(current.getMonth() + 1);
      nextMonth.getTime() / 1000 < dateRange.end &&
        dateIntervals.push(nextMonth);
      current = nextMonth;
    }
  }

  /** create an array of objects (representing each time "bucket") to represent each date interval in a graph */
  const dateAccumulator = new Array<DateAccumulatorInterface>();
  dateIntervals.map((interval) =>
    dateAccumulator.push({
      date: interval,
      time: 0,
      problems: 0,
    })
  );

  const intervalSpan =
    interval === "hour"
      ? HOUR
      : interval === "day"
      ? DAY
      : interval === "week"
      ? WEEK
      : MONTH;

  let index = 0;
  data
    .sort((a, b) => a.time - b.time) // sort data (ascending) by timestamp
    .forEach((entry: UsageDataInterface) => {
      const entryTimeMs = entry.time * 1000;

      // Skip entries before the first bucket - should be enforced by the API, but just in case
      if (entryTimeMs < dateAccumulator[0].date.getTime()) {
        return;
      }

      // Skip entries after the last bucket's interval - should be enforced by the API, but just in case
      if (
        entryTimeMs >
        dateAccumulator[dateAccumulator.length - 1].date.getTime() +
          intervalSpan * 1000
      ) {
        return;
      }

      // Find the correct bucket
      while (
        index < dateAccumulator.length - 1 &&
        entryTimeMs >= dateAccumulator[index + 1].date.getTime()
      ) {
        index++;
      }

      dateAccumulator[index].time += entry.stats.t / 60;
      dateAccumulator[index].problems += entry.stats.n;
    });

  const aggregateTimeAccrued = dateAccumulator.map((entry) =>
    Math.round(entry.time)
  );
  const aggregateProblemsSolved = dateAccumulator.map(
    (entry) => entry.problems
  );
  const aggregateDateLabels = getAggregateDateLabels({
    interval: interval,
    acc: dateAccumulator.map((entry) => entry.date),
  });

  return {
    acc: dateAccumulator,
    aggregateDateLabels,
    aggregateTimeAccrued,
    aggregateProblemsSolved,
  };
};

export const formatBasicData = ({ data }: { data: { data: any } }) =>
  /**
   * @param data nested object returned from /usage endpoint, without series? query param
   * @returns a cleaned array of data that can be passed to accumulateUsageData()
   */
  Object.entries(data.data).map((entry: any) => ({
    time: parseInt(entry[0]),
    stats: {
      t: entry[1].t,
      n: entry[1].n,
    },
  }));

export const formattedLabelValue = (tooltipItem: any) => {
  return (
    tooltipItem.dataset.label + ": " + formatNumber(Math.floor(tooltipItem.raw))
  );
};

export const dataTabs = [
  {
    name: "Time Spent",
    slug: "time-spent",
  },
  {
    name: "Problems Solved",
    slug: "problems-solved",
  },
  {
    name: "Compare Time Spent",
    slug: "compare-time-spent",
  },
  {
    name: "Compare Problems Solved",
    slug: "compare-problems-solved",
  },
];

const tabs = [
  {
    name: "Schools",
    roles: ["district", "super_district", "school", "super_school"],
  },
  {
    name: "Teachers",
    roles: ["district", "super_district", "school", "super_school"],
  },
  {
    name: "Sections",
    roles: ["district", "super_district", "school", "super_school"],
  },
];

// Take Received IDs and Table Data, return an array of the object properties specified
export const setIdsArray = (
  selectedItems: any,
  tableData: any,
  objectProperties: string[]
) => {
  const selectedDataIds = Object.keys(selectedItems);
  const selectedIds = [];
  if (Array.isArray(objectProperties)) {
    for (let j = 0; j < selectedDataIds.length; j++) {
      const arrayId = parseInt(selectedDataIds[j]);
      // initialize an empty object to push into final array
      const element = JSON.parse("{}");
      for (let i = 0; i < objectProperties.length; i++) {
        element[objectProperties[i].toString()] =
          tableData[arrayId][objectProperties[i]];
      }
      selectedIds.push(element);
    }
  }

  return selectedIds;
};
